import React, { useMemo } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { useUIStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import { BORDER_RADIUS, MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import useResponsiveness from '@/hooks/useResponsiveness';

const createStyles = (colors, isXS, messageWidth) => StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: isXS ? 15 : 20,
    paddingVertical: 6,
    marginBottom: 8,
    maxWidth: messageWidth === MESSAGE_WIDTH.NARROW ? MESSAGE_WIDTH_AMOUNTS.NARROW
      : messageWidth === MESSAGE_WIDTH.MEDIUM ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
      : '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    flexShrink: 0,
    position: 'relative',
    zIndex: 12,
    ...(Platform.OS === 'web' ? { boxSizing: 'border-box' } : {}),
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingLeft: 14,
    paddingRight: 8,
    paddingVertical: 7,
    borderRadius: BORDER_RADIUS.round,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
    maxWidth: '100%',
    flexShrink: 1,
    overflow: 'hidden',
  },
  promptLabel: {
    flexShrink: 1,
  },
});

/**
 * One-line "Move this chat to <collection>?" prompt shown above the composer.
 * Rendered at most once per conversation ever (server-enforced); accepting
 * moves the chat, dismissing hides it permanently for this chat.
 */
const CollectionSuggestionBanner = ({ suggestion, isResponding, onAccept, onDismiss }) => {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const messageWidth = useUIStore(state => state.messageWidth);
  const styles = useMemo(
    () => createStyles(colors, isXS, messageWidth),
    [colors, isXS, messageWidth]
  );

  if (!suggestion) return null;

  return (
    <View style={styles.container}>
      <View style={styles.banner}>
        <Icon name="folder" size={12} color={colors.text_color_secondary} />
        <Text size="s" color="secondary" numberOfLines={1} style={styles.promptLabel}>
          {t('collections.suggestion.prompt', { title: suggestion.collectionTitle })}
        </Text>
        <Button
          text={t('collections.suggestion.move')}
          variant="primary"
          size="small"
          isRound
          isLoading={isResponding}
          onPress={onAccept}
        />
        <Button
          variant="secondary"
          size="small"
          isRound
          prependIcon="xmark"
          onPress={onDismiss}
          isDisabled={isResponding}
          tooltip={t('collections.suggestion.dismiss')}
        />
      </View>
    </View>
  );
};

export default React.memo(CollectionSuggestionBanner);
